// AlignUI BudgetOverviewChart v0.0.0
//
// Столбики «как менялось по месяцам»: каждый месяц — один столбец, поделённый на ряды.
// Ряды складываются в целое, поэтому вид годится ровно для тех величин, что сходятся в
// сумму (правило «числа в ряд человек читает как формулу»).
//
// Две правки против исходника набора, обе — подстановка НАШИХ данных вместо демонстрационных:
//
//   1. Ряды (`config`) приходят снаружи. У AlignUI они зашиты в файл как income/expenses/
//      scheduled с английскими подписями — то есть это часть демонстрации, а не механики.
//   2. Подпись оси. Исходник резал название месяца до первой буквы (`value.slice(0, 1)`) и
//      считал деления через `Intl.NumberFormat('en')`. По-русски первая буква не различает
//      ни март с маем, ни июнь с июлем, а деления оси обязаны читаться теми же словами, что
//      суммы в кабинете. Обе подписи задаёт вызывающий.

import { Bar, BarChart, type BarProps, XAxis, YAxis } from 'recharts';

import { ChartContainer, type ChartConfig } from '@/shared/ui/chart';

import useBreakpoint from './use-breakpoint';

const GAP = 2;

export type StackBarChartProps = {
  /** Строка на месяц: подпись оси плюс по числу на каждый ряд из `config`. */
  data: Record<string, string | number>[];
  /** Ряды снизу вверх: ключ в данных, подпись легенды, цвет токеном темы. */
  config: ChartConfig;
  /** Поле с подписью столбца. */
  index?: string;
  /** Как подписать деление оси значений. */
  formatTick?: (value: number) => string;
  className?: string;
};

export function StackBarChart({
  data,
  config,
  index = 'label',
  formatTick = (value) => String(value),
  className,
}: StackBarChartProps) {
  const { md, lg } = useBreakpoint();
  const ряды = Object.keys(config);

  return (
    <ChartContainer config={config} className={className ?? 'h-[212px] w-full'}>
      <BarChart
        data={data}
        barCategoryGap={lg ? 12 : md ? 4 : 2}
        margin={{
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <XAxis dataKey={index} tickLine={false} tickMargin={12} axisLine={false} />
        <YAxis
          width={48}
          tickLine={false}
          axisLine={false}
          tickMargin={0}
          tickFormatter={(value) => formatTick(Number(value))}
        />
        {ряды.map((dataKey, i) => {
          return (
            <Bar
              key={dataKey}
              stackId='a'
              fill={config[dataKey]?.color}
              dataKey={dataKey}
              shape={(props: BarProps) => {
                const { fill, x, width, height } = props;
                const y = Number(props.y);

                const isFirst = i === 0;
                const isLast = i === ряды.length - 1;
                const computedHeight = isLast || isFirst ? height! - GAP / 2 : height! - GAP;

                return (
                  <>
                    {isLast && (
                      <rect
                        fill='hsl(var(--bg-weak-50))'
                        x={x}
                        y={0}
                        width={width}
                        height={y - GAP}
                      />
                    )}
                    <rect
                      fill={fill}
                      x={x}
                      y={isLast ? y : y + GAP / 2}
                      width={width}
                      height={computedHeight}
                    />
                  </>
                );
              }}
            />
          );
        })}
      </BarChart>
    </ChartContainer>
  );
}
